<template>
  <div>
    <ClinicPageHeader
      title="Configurações"
      subtitle="Usuários, permissões, unidades e especialidades"
    />

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar as configurações"
        message="Ocorreu um erro ao buscar as configurações da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="card">
          <div class="skel-line" style="width:40%;margin-bottom:20px" />
          <div class="skel-line" style="width:100%;height:240px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <div v-else class="cfg-grid">
        <nav class="cfg-nav">
          <button
            v-for="item in visibleNav"
            :key="item.id"
            :class="{ on: section === item.id }"
            @click="section = item.id"
          >
            <span v-html="item.icon" />
            {{ item.label }}
          </button>
        </nav>

        <div class="acct-main">
          <!-- Usuários -->
          <div v-if="section === 'u'" class="cfg-panel">
            <div class="card card--flush">
              <div class="card-head flush">
                <h3>Usuários da clínica</h3>
                <!-- Proto usa btn--accent; regra do módulo troca CTA para btn--primary -->
                <div class="row" style="gap:var(--space-2)">
                  <button
                    class="btn btn--outline btn--sm"
                    data-test="new-account"
                    @click="toggleAccountForm"
                  >
                    Criar acesso direto
                  </button>
                  <button
                    class="btn btn--primary btn--sm"
                    @click="toggleInviteForm"
                  >
                    + Convidar usuário
                  </button>
                </div>
              </div>

              <!-- Senha provisória (aparece uma vez) -->
              <div
                v-if="credential"
                class="access-note access-note--warn"
                data-test="temporary-password"
              >
                <p>
                  Acesso criado para
                  <b>{{ credential.name }}</b>
                  ({{ credential.email }}). Senha provisória:
                  <code class="access-note__code">
                    {{ credential.password }}
                  </code>
                </p>
                <p class="access-note__hint">
                  Anote ou copie agora: ela não aparece de novo. No primeiro
                  acesso a pessoa troca a senha.
                </p>
                <div class="row" style="gap:var(--space-2)">
                  <button
                    class="btn btn--outline btn--sm"
                    @click="copy(credential.password, 'Senha copiada')"
                  >
                    Copiar senha
                  </button>
                  <button
                    class="btn btn--ghost btn--sm"
                    @click="dismissCredential"
                  >
                    Fechar
                  </button>
                </div>
              </div>

              <!-- Link do convite recém-criado -->
              <div
                v-if="lastInvite"
                class="access-note"
                data-test="new-invite-link"
              >
                <p>
                  Convite de
                  <b>{{ lastInvite.name }}</b>
                  criado. Envie o link por onde preferir:
                  <code class="access-note__code">{{ lastInvite.url }}</code>
                </p>
                <div class="row" style="gap:var(--space-2)">
                  <button
                    class="btn btn--outline btn--sm"
                    @click="copy(lastInvite.url, 'Link copiado')"
                  >
                    Copiar link
                  </button>
                  <button
                    class="btn btn--ghost btn--sm"
                    @click="lastInvite = null"
                  >
                    Fechar
                  </button>
                </div>
              </div>

              <!-- Criar acesso direto (senha provisória) -->
              <div
                v-if="showAccount"
                class="invite-form"
                style="display:flex;gap:var(--space-3);padding:var(--space-4) var(--space-5);flex-wrap:wrap;align-items:flex-end;border-bottom:1px solid var(--color-gray-100)"
              >
                <div class="field" style="flex:1;min-width:160px">
                  <label class="label">Nome</label>
                  <input
                    v-model="newAccount.name"
                    class="input"
                    placeholder="Nome completo"
                  />
                </div>
                <div class="field" style="flex:1;min-width:180px">
                  <label class="label">E-mail (login)</label>
                  <input
                    v-model="newAccount.email"
                    class="input"
                    type="email"
                    placeholder="email@clinica.com"
                  />
                </div>
                <div class="field" style="min-width:150px">
                  <label class="label">Telefone (opcional)</label>
                  <input v-model="newAccount.phone" class="input" />
                </div>
                <div class="field" style="min-width:150px">
                  <label class="label">Perfil</label>
                  <select v-model="newAccount.role" class="select">
                    <option value="atendimento">Atendimento</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <button
                  class="btn btn--primary btn--sm"
                  :disabled="creatingAccount"
                  @click="submitAccount"
                >
                  {{ creatingAccount ? "Criando…" : "Criar acesso" }}
                </button>
              </div>

              <!-- Formulário de convite -->
              <div
                v-if="showInvite"
                class="invite-form"
                style="display:flex;gap:var(--space-3);padding:var(--space-4) var(--space-5);flex-wrap:wrap;align-items:flex-end;border-bottom:1px solid var(--color-gray-100)"
              >
                <div class="field" style="flex:1;min-width:160px">
                  <label class="label">Nome</label>
                  <input
                    v-model="newOperator.name"
                    class="input"
                    placeholder="Nome completo"
                  />
                </div>
                <div class="field" style="flex:1;min-width:180px">
                  <label class="label">E-mail</label>
                  <input
                    v-model="newOperator.email"
                    class="input"
                    type="email"
                    placeholder="email@clinica.com"
                  />
                </div>
                <div class="field" style="min-width:150px">
                  <label class="label">Perfil</label>
                  <select v-model="newOperator.role" class="select">
                    <option value="atendimento">Atendimento</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <label class="access-check">
                  <input v-model="newOperator.sendEmail" type="checkbox" />
                  Enviar também por e-mail
                </label>
                <button class="btn btn--primary btn--sm" @click="submitInvite">
                  Criar convite
                </button>
              </div>

              <table class="table" style="margin-top:var(--space-2)">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>E-mail</th>
                    <th>Perfil</th>
                    <th>Status</th>
                    <th class="t-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in users" :key="u.id">
                    <td>
                      <div class="row" style="gap:var(--space-3)">
                        <div class="avatar avatar--sm" :class="u.avatarClass">
                          {{ u.initials }}
                        </div>
                        <span class="cell-strong">{{ u.name }}</span>
                      </div>
                    </td>
                    <td class="num">{{ u.email }}</td>
                    <td>
                      <span
                        class="badge badge--no-dot"
                        :class="`badge--${u.roleVariant}`"
                        :style="u.roleStyle"
                      >
                        {{ u.role }}
                      </span>
                    </td>
                    <td>
                      <span class="badge" :class="`badge--${u.statusVariant}`">
                        {{ u.status }}
                      </span>
                    </td>
                    <td class="t-right">
                      <div
                        v-if="u.statusKey === 'convidado'"
                        class="row user-actions"
                      >
                        <button
                          v-if="u.inviteUrl"
                          class="btn btn--ghost btn--sm"
                          data-test="copy-link"
                          @click="copy(u.inviteUrl, 'Link copiado')"
                        >
                          Copiar link
                        </button>
                        <button
                          class="btn btn--ghost btn--sm"
                          data-test="resend"
                          :disabled="busyUserId === u.id"
                          @click="resendInvitation(u)"
                        >
                          Reenviar
                        </button>
                        <button
                          class="btn btn--ghost btn--sm user-actions__danger"
                          data-test="cancel-invite"
                          @click="askUserAction('cancel', u)"
                        >
                          Cancelar convite
                        </button>
                      </div>
                      <div v-else class="row user-actions">
                        <button
                          class="btn btn--ghost btn--sm"
                          data-test="change-role"
                          @click="askUserAction('role', u)"
                        >
                          Alterar perfil
                        </button>
                        <button
                          class="btn btn--ghost btn--sm user-actions__danger"
                          data-test="remove"
                          @click="askUserAction('remove', u)"
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Permissões -->
          <div v-else-if="section === 'p'" class="cfg-panel">
            <div class="card card--flush">
              <div class="card-head flush"><h3>Permissões por perfil</h3></div>
              <table class="table" style="margin-top:var(--space-2)">
                <thead>
                  <tr>
                    <th>Área</th>
                    <th style="text-align:center">Admin</th>
                    <th>Atendimento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="res in resourceRows" :key="res.key">
                    <td class="cell-strong">{{ res.label }}</td>
                    <td style="text-align:center" data-test="perm-admin">
                      <span class="perm-yes" aria-label="Acesso total">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          style="display:inline"
                          aria-hidden="true"
                        >
                          <path
                            d="M20 6L9 17l-5-5"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                    </td>
                    <td>
                      <ClinicSeg
                        :value="levelOf('atendimento', res.key)"
                        :options="levelOptions(res)"
                        @input="v => setLevel(res.key, v)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <p class="perm-note">
                Admin tem acesso total. Usuários e permissões são sempre só do
                Admin. As mudanças valem só para esta clínica.
              </p>
            </div>
          </div>

          <!-- Unidades -->
          <div v-else-if="section === 'un'" class="cfg-panel">
            <div class="card card--flush">
              <div class="card-head flush">
                <h3>Unidades</h3>
                <button
                  v-if="canEditConfig"
                  class="btn btn--primary btn--sm"
                  data-test="new-unit"
                  @click="newUnit"
                >
                  + Nova unidade
                </button>
              </div>

              <!-- Formulário de unidade (nova/editar) -->
              <div v-if="unitForm" class="unit-form">
                <div class="form-grid">
                  <div class="field">
                    <label class="label">Nome da unidade</label>
                    <input
                      v-model="unitForm.name"
                      class="input"
                      :class="{ 'input--error': unitNameError }"
                      placeholder="Ex.: Unidade Zona Sul"
                    />
                    <span v-if="unitNameError" class="field-error">
                      Informe o nome da unidade.
                    </span>
                  </div>
                  <div class="field">
                    <label class="label">Telefone</label>
                    <input
                      v-model="unitForm.phone"
                      v-mask="['(##) ####-####', '(##) #####-####']"
                      class="input"
                      :class="{ 'input--error': phoneError }"
                    />
                    <span v-if="phoneError" class="field-error">
                      {{ phoneError }}
                    </span>
                  </div>
                  <div class="field">
                    <label class="label">CNPJ</label>
                    <input
                      v-model="unitForm.cnpj"
                      v-mask="'##.###.###/####-##'"
                      class="input"
                      :class="{ 'input--error': cnpjError }"
                    />
                    <span v-if="cnpjError" class="field-error">
                      {{ cnpjError }}
                    </span>
                  </div>
                  <div class="field">
                    <label class="label">E-mail</label>
                    <input
                      v-model="unitForm.email"
                      class="input"
                      type="email"
                    />
                  </div>
                  <div class="field">
                    <label class="label">CEP</label>
                    <input
                      v-model="unitForm.addressDetail.zipCode"
                      class="input"
                      :class="{ 'input--error': cepError }"
                      placeholder="00000-000"
                      @blur="fillAddressFromCep"
                    />
                    <span v-if="cepLoading" class="field-hint">
                      Buscando endereço…
                    </span>
                    <span v-else-if="cepError" class="field-error">
                      {{ cepError }}
                    </span>
                  </div>
                  <div class="field">
                    <label class="label">Endereço</label>
                    <input
                      v-model="unitForm.addressDetail.street"
                      class="input"
                    />
                  </div>
                  <div class="field">
                    <label class="label">Número</label>
                    <input
                      v-model="unitForm.addressDetail.number"
                      class="input"
                    />
                  </div>
                  <div class="field">
                    <label class="label">Complemento</label>
                    <input
                      v-model="unitForm.addressDetail.complement"
                      class="input"
                    />
                  </div>
                  <div class="field">
                    <label class="label">Bairro</label>
                    <input
                      v-model="unitForm.addressDetail.neighborhood"
                      class="input"
                    />
                  </div>
                  <div class="field">
                    <label class="label">Cidade / UF</label>
                    <div class="row" style="gap:var(--space-2)">
                      <input
                        v-model="unitForm.addressDetail.city"
                        class="input"
                        style="flex:1"
                      />
                      <input
                        v-model="unitForm.addressDetail.state"
                        class="input"
                        maxlength="2"
                        style="width:64px"
                      />
                    </div>
                  </div>
                </div>
                <div class="setting-row" style="margin-top:var(--space-4)">
                  <div>
                    <b>Unidade sede</b>
                    <span class="field-hint">
                      A clínica sempre tem uma sede. Marcar esta desmarca a
                      atual.
                    </span>
                  </div>
                  <ClinicToggle
                    :value="unitForm.headquarter"
                    @input="v => (unitForm.headquarter = v)"
                  />
                </div>
                <div class="row unit-form__actions">
                  <button
                    v-if="unitForm.id && !unitForm.wasHeadquarter"
                    class="btn btn--ghost btn--sm user-actions__danger"
                    data-test="remove-unit"
                    @click="unitRemoving = unitForm"
                  >
                    Desativar unidade
                  </button>
                  <span style="flex:1" />
                  <button
                    class="btn btn--outline btn--sm"
                    :disabled="savingUnit"
                    @click="unitForm = null"
                  >
                    Cancelar
                  </button>
                  <button
                    class="btn btn--primary btn--sm"
                    data-test="save-unit"
                    :disabled="savingUnit"
                    @click="saveUnit"
                  >
                    {{ savingUnit ? "Aguarde…" : "Salvar" }}
                  </button>
                </div>
              </div>
              <table class="table" style="margin-top:var(--space-2)">
                <thead>
                  <tr>
                    <th>Unidade</th>
                    <th>Endereço</th>
                    <th>Profissionais de saúde</th>
                    <th>Status</th>
                    <th class="t-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in unitRows" :key="u.id">
                    <td class="cell-strong">
                      {{ u.label }}
                      <span
                        v-if="u.headquarter"
                        class="badge badge--info badge--no-dot"
                        style="margin-left:var(--space-2)"
                      >
                        Sede
                      </span>
                    </td>
                    <td style="color:var(--color-gray-500)">{{ u.address }}</td>
                    <td class="num">{{ u.doctorCount }}</td>
                    <td><span class="badge badge--success">Ativa</span></td>
                    <td class="t-right">
                      <button
                        v-if="canEditConfig"
                        class="btn btn--ghost btn--sm"
                        data-test="edit-unit"
                        @click="editUnit(u)"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Especialidades -->
          <div v-else-if="section === 'e'" class="cfg-panel">
            <div class="card">
              <div class="card-head">
                <h3>Especialidades atendidas</h3>
                <button
                  v-if="canEditConfig"
                  class="btn btn--primary btn--sm"
                  data-test="add-specialty"
                  @click="addSpecialty"
                >
                  + Adicionar
                </button>
              </div>

              <div v-if="showSpecialtyForm" class="row specialty-form">
                <div class="field" style="flex:1;min-width:180px">
                  <label class="label">Especialidade</label>
                  <select v-model="newSpecialty.specialtyId" class="select">
                    <option value="" disabled>Selecione…</option>
                    <option
                      v-for="sp in specialties"
                      :key="sp.id"
                      :value="sp.id"
                    >
                      {{ sp.name }}
                    </option>
                  </select>
                </div>
                <div class="field" style="width:180px">
                  <label class="label">Duração padrão (min)</label>
                  <input
                    v-model="newSpecialty.duration"
                    class="input"
                    type="number"
                    min="5"
                    max="480"
                    placeholder="Ex.: 30"
                  />
                </div>
                <button
                  class="btn btn--primary btn--sm"
                  :disabled="!newSpecialty.specialtyId || savingSpecialty"
                  @click="submitSpecialty"
                >
                  {{ savingSpecialty ? "Aguarde…" : "Adicionar" }}
                </button>
              </div>

              <p v-if="hasSuggestedOnly" class="field-hint specialty-hint">
                Sugeridas a partir dos profissionais de saúde da clínica.
                Adicione para definir a duração padrão.
              </p>
              <div class="cond-chips">
                <span
                  v-for="sp in clinicSpecialties"
                  :key="sp.specialtyId"
                  data-test="specialty-chip"
                  :class="{ 'chip--suggested': sp.suggested }"
                >
                  {{ specialtyLabel(sp) }}
                  <button
                    v-if="sp.id && canEditConfig"
                    class="chip-x"
                    data-test="remove-specialty"
                    :aria-label="`Remover ${sp.name}`"
                    @click="removeSpecialty(sp)"
                  >
                    ×
                  </button>
                </span>
                <span v-if="!clinicSpecialties.length" class="field-hint">
                  Nenhuma especialidade cadastrada.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ClinicConfirmDialog
      v-if="unitRemoving"
      title="Desativar unidade"
      :message="
        `Desativar a ${unitRemoving.name}? Ela deixa de aparecer na clínica.`
      "
      confirm-label="Desativar"
      variant="danger"
      :loading="savingUnit"
      @confirm="removeUnit"
      @close="unitRemoving = null"
    />
    <ClinicConfirmDialog
      v-if="userAction"
      :title="userActionMeta.title"
      :message="userActionMeta.message"
      :confirm-label="userActionMeta.confirmLabel"
      :variant="userActionMeta.variant"
      :loading="!!busyUserId"
      @confirm="runUserAction"
      @close="userAction = null"
    />
  </div>
</template>

<script>
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicToggle from "@/components/clinica/ClinicToggle.vue";
import ClinicConfirmDialog from "@/components/clinica/ClinicConfirmDialog.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";
import { ClinicConfig as ConfigService } from "@/services/clinica";

// Acesso pela área `config` da clínica ativa (US-C11). Usuários e Permissões
// são sempre só do Admin; Unidades e Especialidades seguem o nível da área.
const ADMIN_SECTIONS = ["u", "p"];
const LEVEL_LABEL = { none: "Nenhum", view: "Ver", edit: "Editar" };

// Ícones da sub-navegação lateral (config.html).
const NAV_ICONS = {
  u: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  p: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" stroke-width="2"/></svg>`,
  un: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 21h18M5 21V7l7-4 7 4v14" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M9 21v-6h6v6" stroke="currentColor" stroke-width="2"/></svg>`,
  e: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 3v6a6 6 0 0 0 12 0V3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="18" cy="17" r="3" stroke="currentColor" stroke-width="2"/></svg>`
};

// role → badge visual e rótulo de coluna.
const ROLE_VARIANT = {
  admin: {
    variant: "accent",
    style: "background:var(--color-accent-light);color:var(--color-accent-dark)"
  },
  atendimento: { variant: "info", style: "" },
  financeiro: { variant: "neutral", style: "" },
  medico: { variant: "neutral", style: "" }
};
const ROLE_LABEL = {
  admin: "Admin",
  atendimento: "Atendimento",
  financeiro: "Financeiro",
  medico: "Profissional de saúde"
};
const STATUS_META = {
  ativo: { label: "Ativo", variant: "success", action: "Editar" },
  convidado: {
    label: "Convite pendente",
    variant: "warning",
    action: "Reenviar"
  }
};
const AVATARS = ["av-rose", "av-blue", "av-green", "av-amber", "av-slate"];

// Mensagem simples da API (`render json: { error: "..." }`).
// Rótulos dos campos que a API devolve em `errors` (record_invalid).
const FIELD_LABEL = {
  name: "Nome",
  "address.zip_code": "CEP",
  "address.street": "Endereço",
  "address.street_number": "Número",
  "address.neighborhood": "Bairro",
  "address.city": "Cidade",
  "address.state": "UF"
};

function apiErrorMessage(error) {
  const body = error && error.response && error.response.data;
  if (!body) return "";
  if (typeof body.error === "string") return body.error;
  if (body.errors && typeof body.errors === "object") {
    return Object.entries(body.errors)
      .map(
        ([field, msgs]) =>
          `${FIELD_LABEL[field] || field} ${[].concat(msgs)[0]}.`
      )
      .join(" ");
  }
  return "";
}

function mapOperator(op, i) {
  const role = ROLE_VARIANT[op.role] || { variant: "neutral", style: "" };
  const st = STATUS_META[op.status] || {
    label: op.status,
    variant: "neutral",
    action: "Editar"
  };
  return {
    id: op.id,
    roleKey: op.role,
    statusKey: op.status,
    name: op.name,
    initials: op.initials,
    avatarClass: op.avatarClass || AVATARS[i % AVATARS.length],
    email: op.email,
    role: op.roleLabel || ROLE_LABEL[op.role] || op.role,
    roleVariant: role.variant,
    roleStyle: role.style,
    status: st.label,
    statusVariant: st.variant,
    action: st.action,
    inviteUrl: op.invite_url || null
  };
}

export default {
  name: "ClinicConfig",
  components: {
    ClinicPageHeader,
    ClinicStateBox,
    ClinicToggle,
    ClinicSeg,
    ClinicConfirmDialog
  },
  data() {
    return {
      // loading → ready/error. Preview de estados via ?state= na URL.
      state: "loading",
      section: "u",
      navItems: [
        { id: "u", label: "Usuários", icon: NAV_ICONS.u },
        { id: "p", label: "Permissões", icon: NAV_ICONS.p },
        { id: "un", label: "Unidades", icon: NAV_ICONS.un },
        { id: "e", label: "Especialidades", icon: NAV_ICONS.e }
      ],
      users: [],
      resourceRows: [],
      permMatrix: [],
      units: [],
      specialties: [],
      clinicSpecialties: [],
      showSpecialtyForm: false,
      savingSpecialty: false,
      newSpecialty: { specialtyId: "", duration: "" },
      showInvite: false,
      // Ação pendente de confirmação na equipe: { kind: role|remove|cancel, user }
      userAction: null,
      busyUserId: null,
      unitForm: null,
      unitTouched: false,
      unitRemoving: null,
      savingUnit: false,
      cepLoading: false,
      cepError: "",
      newOperator: {
        name: "",
        email: "",
        role: "atendimento",
        sendEmail: true
      },
      // US-C12: acesso sem depender de e-mail
      showAccount: false,
      creatingAccount: false,
      newAccount: { name: "", email: "", phone: "", role: "atendimento" },
      credential: null,
      lastInvite: null
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.state = "loading";
      try {
        // Serviços de config são somente-leitura; preview de erro via ?state.
        if (this.$route.query.state === "error") throw new Error("preview");
        if (!this.isAdmin) this.section = "un";
        const [ops, perms, un, sp, csp] = await Promise.all([
          this.isAdmin ? ConfigService.operators() : { data: [] },
          ConfigService.permissions(),
          ConfigService.units(),
          ConfigService.specialties(),
          ConfigService.clinicSpecialties()
        ]);
        this.users = ops.data.map((o, i) => mapOperator(o, i));
        this.resourceRows = perms.data.resources;
        this.permMatrix = perms.data.matrix;
        this.units = un.data;
        this.specialties = sp.data;
        this.clinicSpecialties = csp.data;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    roleLabel(role) {
      return ROLE_LABEL[role] || role;
    },
    levelOf(role, resource) {
      const p = this.permMatrix.find(
        m => m.role === role && m.resource === resource
      );
      return (p && p.level) || "none";
    },
    levelOptions(res) {
      return (res.levels || []).map(value => ({
        value,
        label: LEVEL_LABEL[value] || value
      }));
    },
    // Salva o nível do Atendimento; a matriz só muda com a resposta da API.
    async setLevel(resource, level) {
      if (this.levelOf("atendimento", resource) === level) return;
      try {
        const { data } = await ConfigService.setPermission({ resource, level });
        this.permMatrix = data.matrix;
        this.$toast.success("Permissão atualizada");
      } catch (e) {
        this.$toast.error("Não foi possível atualizar a permissão");
      }
    },
    toggleInviteForm() {
      this.showInvite = !this.showInvite;
      this.showAccount = false;
    },
    toggleAccountForm() {
      this.showAccount = !this.showAccount;
      this.showInvite = false;
    },
    async copy(text, message) {
      try {
        await this.$copyText(text);
        this.$toast.success(message);
      } catch (e) {
        this.$toast.error(
          "Não foi possível copiar. Selecione e copie o texto."
        );
      }
    },
    dismissCredential() {
      this.credential = null;
    },
    async submitAccount() {
      const { name, email, phone, role } = this.newAccount;
      if (!name.trim() || !email.trim() || !email.includes("@")) {
        this.$toast.error("Preencha nome e e-mail válidos");
        return;
      }
      this.creatingAccount = true;
      try {
        const { data } = await ConfigService.createOperatorAccount({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          role
        });
        this.users.push(mapOperator(data, this.users.length));
        this.credential = {
          name: data.name,
          email: data.email,
          password: data.temporary_password
        };
        this.newAccount = {
          name: "",
          email: "",
          phone: "",
          role: "atendimento"
        };
        this.showAccount = false;
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível criar o acesso."
        );
      } finally {
        this.creatingAccount = false;
      }
    },
    async submitInvite() {
      const { name, email, role, sendEmail } = this.newOperator;
      if (!name.trim() || !email.trim() || !email.includes("@")) {
        this.$toast.error("Preencha nome e e-mail válidos");
        return;
      }
      try {
        const { data } = await ConfigService.inviteOperator({
          name: name.trim(),
          email: email.trim(),
          role,
          ...(sendEmail === false ? { sendEmail: false } : {})
        });
        this.users.push(mapOperator(data, this.users.length));
        if (data.invite_url) {
          this.lastInvite = { name: data.name, url: data.invite_url };
        }
        this.newOperator = {
          name: "",
          email: "",
          role: "atendimento",
          sendEmail: true
        };
        this.showInvite = false;
        this.$toast.success(
          sendEmail === false ? "Convite criado" : "Convite enviado"
        );
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível criar o convite"
        );
      }
    },
    askUserAction(kind, user) {
      this.userAction = { kind, user };
    },
    async runUserAction() {
      const { kind, user } = this.userAction;
      this.busyUserId = user.id;
      try {
        if (kind === "role") {
          const role = user.roleKey === "admin" ? "atendimento" : "admin";
          const { data } = await ConfigService.updateOperator(user.id, role);
          const i = this.users.findIndex(u => u.id === user.id);
          this.users.splice(i, 1, {
            ...mapOperator(data, i),
            avatarClass: user.avatarClass
          });
          this.$toast.success("Perfil atualizado");
        } else if (kind === "remove") {
          await ConfigService.removeOperator(user.id);
          this.users = this.users.filter(u => u.id !== user.id);
          this.$toast.success("Usuário removido");
        } else {
          await ConfigService.cancelInvitation(user.id);
          this.users = this.users.filter(u => u.id !== user.id);
          this.$toast.success("Convite cancelado");
        }
        this.userAction = null;
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível concluir. Tente novamente."
        );
        this.userAction = null;
      } finally {
        this.busyUserId = null;
      }
    },
    async resendInvitation(user) {
      this.busyUserId = user.id;
      try {
        await ConfigService.resendInvitation(user.id);
        this.$toast.success("Convite reenviado");
      } catch (e) {
        this.$toast.error("Não foi possível reenviar o convite");
      } finally {
        this.busyUserId = null;
      }
    },
    newUnit() {
      this.openUnitForm({
        name: "",
        phone: "",
        cnpj: "",
        email: "",
        headquarter: false,
        addressDetail: null
      });
    },
    editUnit(row) {
      const unit = this.units.find(u => u.id === row.id) || row;
      this.openUnitForm(unit);
    },
    openUnitForm(unit) {
      const a = unit.addressDetail || {};
      this.unitForm = {
        id: unit.id,
        name: unit.name || "",
        phone: unit.phone || "",
        cnpj: unit.cnpj || "",
        email: unit.email || "",
        headquarter: !!unit.headquarter,
        wasHeadquarter: !!unit.headquarter,
        addressDetail: {
          zipCode: a.zipCode || "",
          street: a.street || "",
          number: a.number || "",
          complement: a.complement || "",
          neighborhood: a.neighborhood || "",
          city: a.city || "",
          state: a.state || ""
        }
      };
      this.unitTouched = false;
      this.cepError = "";
    },
    async fillAddressFromCep() {
      const zip = this.unitForm.addressDetail.zipCode;
      if (String(zip).replace(/\D/g, "").length !== 8) return;
      this.cepLoading = true;
      this.cepError = "";
      try {
        const { data } = await ConfigService.lookupCep(zip);
        const a = this.unitForm.addressDetail;
        a.street = data.street || a.street;
        a.neighborhood = data.neighborhood || a.neighborhood;
        a.city = data.city || a.city;
        a.state = data.state || a.state;
      } catch (e) {
        this.cepError = "CEP não encontrado.";
      } finally {
        this.cepLoading = false;
      }
    },
    async saveUnit() {
      this.unitTouched = true;
      if (
        !this.unitForm.name.trim() ||
        this.phoneError ||
        this.cnpjError ||
        this.savingUnit
      )
        return;
      this.savingUnit = true;
      try {
        const { data } = await ConfigService.saveUnit(this.unitForm);
        const others = this.units.filter(u => u.id !== data.id);
        // nova sede desmarca a anterior (o backend faz o mesmo)
        if (data.headquarter) others.forEach(u => (u.headquarter = false));
        this.units = [...others, data].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        this.unitForm = null;
        this.$toast.success("Unidade salva");
        this.$store.dispatch("clinica/init");
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível salvar a unidade."
        );
      } finally {
        this.savingUnit = false;
      }
    },
    async removeUnit() {
      const unit = this.unitRemoving;
      this.savingUnit = true;
      try {
        await ConfigService.removeUnit(unit.id);
        this.units = this.units.filter(u => u.id !== unit.id);
        this.unitForm = null;
        this.$toast.success("Unidade desativada");
        this.$store.dispatch("clinica/init");
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível desativar a unidade."
        );
      } finally {
        this.unitRemoving = null;
        this.savingUnit = false;
      }
    },
    addSpecialty() {
      this.newSpecialty = { specialtyId: "", duration: "" };
      this.showSpecialtyForm = true;
    },
    async submitSpecialty() {
      if (!this.newSpecialty.specialtyId || this.savingSpecialty) return;
      this.savingSpecialty = true;
      try {
        const duration = parseInt(this.newSpecialty.duration, 10);
        const { data } = await ConfigService.addClinicSpecialty({
          specialtyId: this.newSpecialty.specialtyId,
          defaultDuration: Number.isNaN(duration) ? null : duration
        });
        // saem as sugestões assim que a clínica cadastra a primeira
        const saved = this.clinicSpecialties.filter(
          sp => !sp.suggested && sp.specialtyId !== data.specialtyId
        );
        this.clinicSpecialties = [...saved, data].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        this.showSpecialtyForm = false;
        this.$toast.success("Especialidade salva");
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível salvar a especialidade."
        );
      } finally {
        this.savingSpecialty = false;
      }
    },
    async removeSpecialty(sp) {
      try {
        await ConfigService.removeClinicSpecialty(sp.id);
        this.clinicSpecialties = this.clinicSpecialties.filter(
          x => x.id !== sp.id
        );
      } catch (e) {
        this.$toast.error("Não foi possível remover a especialidade.");
      }
    },
    specialtyLabel(sp) {
      return sp.defaultDuration
        ? `${sp.name} · ${sp.defaultDuration} min`
        : sp.name;
    }
  },
  computed: {
    isAdmin() {
      return !!this.$store.getters["clinica/isAdmin"];
    },
    canEditConfig() {
      return !!this.$store.getters["clinica/can"]("config", "edit");
    },
    visibleNav() {
      return this.isAdmin
        ? this.navItems
        : this.navItems.filter(i => !ADMIN_SECTIONS.includes(i.id));
    },
    userActionMeta() {
      if (!this.userAction) return {};
      const { kind, user } = this.userAction;
      if (kind === "role") {
        const to = user.roleKey === "admin" ? "Atendimento" : "Admin";
        return {
          title: "Alterar perfil",
          message: `Mudar o perfil de ${user.name} para ${to}?`,
          confirmLabel: "Alterar",
          variant: "primary"
        };
      }
      if (kind === "remove") {
        return {
          title: "Remover usuário",
          message: `Remover o acesso de ${user.name} à clínica?`,
          confirmLabel: "Remover",
          variant: "danger"
        };
      }
      return {
        title: "Cancelar convite",
        message: `Cancelar o convite enviado para ${user.email}?`,
        confirmLabel: "Cancelar convite",
        variant: "danger"
      };
    },
    unitRows() {
      return this.units.map(u => ({
        id: u.id,
        label: u.name,
        address: u.address || "",
        doctorCount: u.doctorCount,
        headquarter: u.headquarter
      }));
    },
    hasSuggestedOnly() {
      return (
        this.clinicSpecialties.length > 0 &&
        this.clinicSpecialties.every(sp => sp.suggested)
      );
    },
    unitNameError() {
      return this.unitTouched && !!this.unitForm && !this.unitForm.name.trim();
    },
    phoneError() {
      if (!this.unitTouched || !this.unitForm || !this.unitForm.phone) return "";
      const digits = this.unitForm.phone.replace(/\D/g, "");
      return digits.length === 10 || digits.length === 11
        ? ""
        : "Telefone inválido.";
    },
    cnpjError() {
      if (!this.unitTouched || !this.unitForm || !this.unitForm.cnpj) return "";
      const digits = this.unitForm.cnpj.replace(/\D/g, "");
      return digits.length === 14 ? "" : "CNPJ inválido.";
    }
  }
};
</script>

<style scoped>
.access-note {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  background: var(--color-primary-light);
  border-bottom: 1px solid var(--color-gray-100);
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
}
.access-note p {
  margin: 0;
}
.access-note--warn {
  background: var(--color-warning-light);
}
.access-note__code {
  display: inline-block;
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-bg-card);
  font-weight: var(--fw-bold);
  word-break: break-all;
}
.access-note__hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}
.access-check {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
}
.perm-note {
  margin: 0;
  padding: var(--space-4) var(--space-5);
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  border-top: 1px solid var(--color-gray-100);
}
.user-actions {
  justify-content: flex-end;
  gap: var(--space-1);
}
.user-actions__danger {
  color: var(--color-danger);
}
.specialty-form {
  gap: var(--space-3);
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: var(--space-4);
}
.specialty-hint {
  margin-bottom: var(--space-3);
}
.chip--suggested {
  border: 1.5px dashed var(--color-gray-300);
}
.chip-x {
  border: none;
  background: none;
  margin-left: var(--space-1);
  color: var(--color-gray-500);
  cursor: pointer;
  font-size: var(--font-size-md);
  line-height: 1;
}
.unit-form {
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-gray-100);
}
.unit-form__actions {
  margin-top: var(--space-4);
  gap: var(--space-2);
}
</style>
